import { useTranslation } from 'react-i18next';
import { PAGE_SIZE } from 'infrastructure/hooks/rpm/overview-dashboard';
import { convertDateToTimeZone, DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';

import s from './styles.module.scss';
import DateCell from '../date-cell';
import StatusCell from '../status-cell';
import ReadingCell from '../reading-cell';
import ThresholdCell from '../threshold-cell';
import CommentsCell from '../comments-cell';

import type { IRpmPatientActivity } from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { FC } from 'react';

interface MobileTableProps {
  page: number;
  activity: IRpmPatientActivity | undefined;
  isLoading: boolean;
  name: string;
}

const MobileTable: FC<MobileTableProps> = ({
  activity,
  isLoading,
  name,
  page,
}) => {
  const { t } = useTranslation();

  const currentPage = page ?? 1;
  const itemIndexPrefix = (currentPage - 1) * (PAGE_SIZE ?? 100);

  return (
    <BaseSpinWrapper spinning={isLoading}>
      <div className={s.wrapper}>
        <h4 className={s.title}>{t('labels.readings')}</h4>

        <div className={s['items-wrapper']}>
          {activity?.data?.map((reading, idx) => {
            const date = new Date(parseInt(reading.createdAt));
            const patientTime = convertDateToTimeZone(
              new Date(),
              reading.patient.timezone,
            );
            const { globalStatus } = reading;
            const createdAt = DateFormatter({
              date: new Date(parseInt(reading.createdAt)),
              format: DateFormats['MM-dd-yyyy | hh:mm a'],
            });
            const subtitle = `#${itemIndexPrefix + idx + 1} ${createdAt}`;

            return (
              <div className={s.item}>
                <div className={s.index}>#{itemIndexPrefix + idx + 1}</div>
                <div className={s.header}>
                  <div className={s.date} data-reading>
                    <p>{t('labels.readingTime')}</p>
                    <DateCell date={date} />
                  </div>
                  <div className={s.date} data-local>
                    <p>{t('labels.localTime')}</p>
                    <DateCell date={patientTime} />
                  </div>
                  <StatusCell
                    wrapperClassName={s.status}
                    globalStatus={globalStatus}
                    thresholds={reading.patient.threshold}
                    thresholdName={reading.thresholdName}
                    mightBeErroneousReason={reading.mightBeErroneousReason}
                    isRecovered={reading.isRecovered}
                    receivedTime={reading.receivedTime}
                    readingTime={reading.dateTime}
                    timezone={reading.patient.timezone}
                  />
                </div>
                <div className={s.body}>
                  <ReadingCell
                    values={reading.value}
                    statuses={reading.statuses}
                    readingType={reading.readingType}
                  />
                  <ThresholdCell
                    values={reading.value}
                    readingType={reading.readingType}
                    thresholds={reading.patient.threshold}
                    statuses={reading.statuses}
                    globalStatus={reading.globalStatus}
                    thresholdName={reading.thresholdName}
                  />
                </div>
                <div className={s.footer}>
                  <CommentsCell
                    comments={reading.comments}
                    title={name}
                    patientGuid={reading.patient.guid}
                    subtitle={subtitle}
                    values={reading.value}
                    readingType={reading.readingType}
                    thresholds={reading.patient.threshold}
                    statuses={reading.statuses}
                    globalStatus={reading.globalStatus}
                    thresholdName={reading.thresholdName}
                    page={page}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </BaseSpinWrapper>
  );
};
export default MobileTable;
